// TradeScreen — buy low, sell high. The Buffett principle for kids: buy something
// for LESS than it's worth, sell it at FAIR value. The market is a shelf of 6 items,
// each with a buy price and a PREDICTED resell range (not a promise). The player
// researches (is the buy price below the range? then it's undervalued), buys up to 3,
// holds 1–3 weeks paying a carrying cost, and sells when the market is hot. Hold too
// long and it depreciates. Profit settles into the weekly cashflow → Payday.

import {
  generateMarket,
  marketCondition,
  marketMood,
  resellValue,
  depreciationFactor,
  carryCost,
  MAX_INVENTORY,
  HOLD_GRACE_WEEKS,
  type TradeOffer,
  type TradeItem,
} from '@biz-kid/engine';
import { useDispatch, useGameState, selectCash, selectTrade } from '@biz-kid/store';
import { Card, Eyebrow, Button, Chip, Money, money } from '../../ui/kit.js';
import { CartoonTradeScene as TradeScene } from '../../ui/cartoon.js';

export function TradeScreen({ onClose }: { onClose: () => void }) {
  const dispatch = useDispatch();
  const trade = useGameState(selectTrade);
  const cash = useGameState(selectCash);
  const week = useGameState((s) => s.clock.week);
  const seed = useGameState((s) => s.rngSeed);

  const offers = generateMarket(seed, week);
  const boughtThisWeek = trade.boughtSlotsWeek === week ? trade.boughtSlots : [];
  const condition = marketCondition(seed, week);
  const mood = marketMood(condition);
  const inventoryFull = trade.inventory.length >= MAX_INVENTORY;

  const moodChip = {
    hot: { tone: 'up' as const, label: '🔥 Hot market — great week to sell' },
    steady: { tone: 'neutral' as const, label: '➖ Steady market' },
    cold: { tone: 'down' as const, label: '🧊 Cold market — a poor week to sell' },
  }[mood];

  return (
    <div className="bk-app" style={{ position: 'fixed', inset: 0, zIndex: 7, overflowY: 'auto' }}>
      <div style={{ maxWidth: 680, margin: '0 auto', padding: 'clamp(14px, 3vw, 26px)', paddingBottom: 90 }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <Eyebrow color="var(--brand)">Skin trade · buy low, flip high</Eyebrow>
            <h1 style={{ margin: '2px 0 0', fontSize: 22, fontWeight: 900, color: 'var(--text)' }}>💎 Skin Trade</h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Chip tone="brand">{money(cash)}</Chip>
            <Button tone="ghost" size="sm" onClick={onClose}>← Home</Button>
          </div>
        </header>

        {/* Studio atmosphere — the skin-trade desk. */}
        <Card pad={0} style={{ overflow: 'hidden', marginBottom: 14 }}>
          <TradeScene />
        </Card>

        {!trade.active ? (
          <Card>
            <p style={{ margin: 0, fontSize: 15, color: 'var(--text)', lineHeight: 1.6 }}>
              Buy rare skins cheap, then flip them when the hype peaks — research and timing, not luck.
            </p>
            <div style={{ marginTop: 16 }}>
              <Button tone="primary" size="lg" onClick={() => dispatch({ kind: 'StartTrade' })}>Start flipping skins →</Button>
            </div>
          </Card>
        ) : (
          <>
            {/* Inventory you hold */}
            <Card style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                <Eyebrow>Your stock ({trade.inventory.length}/{MAX_INVENTORY})</Eyebrow>
                <Chip tone={moodChip.tone}>{moodChip.label}</Chip>
              </div>
              {trade.inventory.length === 0 ? (
                <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-2)' }}>
                  Nothing held yet. Buy an undervalued item from the market below.
                </p>
              ) : (
                <div style={{ display: 'grid', gap: 10 }}>
                  {trade.inventory.map((item) => (
                    <HoldingRow
                      key={item.id}
                      item={item}
                      week={week}
                      condition={condition}
                      onSell={() => dispatch({ kind: 'TradeSell', itemId: item.id })}
                    />
                  ))}
                </div>
              )}
            </Card>

            {/* The weekly market */}
            <Card>
              <Eyebrow color="var(--brand)">This week's market</Eyebrow>
              <p style={{ margin: '6px 0 12px', fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
                💡 A buy price <b style={{ color: 'var(--text)' }}>below</b> the predicted range is undervalued — that's
                the one to grab. Above it, there's no room for profit.
              </p>
              <div style={{ display: 'grid', gap: 10 }}>
                {offers.map((offer) => (
                  <OfferRow
                    key={offer.slot}
                    offer={offer}
                    owned={boughtThisWeek.includes(offer.slot)}
                    canAfford={cash >= offer.buyPrice}
                    inventoryFull={inventoryFull}
                    onBuy={() => dispatch({ kind: 'TradeBuy', slot: offer.slot })}
                  />
                ))}
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function HoldingRow({
  item, week, condition, onSell,
}: {
  item: TradeItem;
  week: number;
  condition: number;
  onSell: () => void;
}) {
  const weeksHeld = week - item.boughtWeek;
  const tooSoon = weeksHeld < 1;
  const sellNow = resellValue(item, Math.max(1, weeksHeld), condition);
  const net = sellNow - item.buyPrice;
  const depreciating = depreciationFactor(item.category, weeksHeld) < 1;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, borderRadius: 14, border: '1px solid var(--border)', background: 'var(--surface-2)' }}>
      <span style={{ fontSize: 24, flexShrink: 0 }}>{item.emoji}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 800, fontSize: 15, color: 'var(--text)' }}>{item.name}</span>
        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-2)' }}>
          Paid {money(item.buyPrice)} · held {weeksHeld}w
          {depreciating && <span style={{ color: 'var(--down)', fontWeight: 700 }}> · ⚠️ depreciating</span>}
        </span>
      </span>
      <span style={{ textAlign: 'right', flexShrink: 0 }}>
        {tooSoon ? (
          <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 700 }}>Hold ≥1 wk</span>
        ) : (
          <>
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Sell now {money(sellNow)}</div>
            <Button tone={net >= 0 ? 'up' : 'soft'} size="sm" onClick={onSell}>
              Sell ({net >= 0 ? '+' : ''}{money(net)})
            </Button>
          </>
        )}
      </span>
    </div>
  );
}

function OfferRow({
  offer, owned, canAfford, inventoryFull, onBuy,
}: {
  offer: TradeOffer;
  owned: boolean;
  canAfford: boolean;
  inventoryFull: boolean;
  onBuy: () => void;
}) {
  // Is it a deal? Buy price below the predicted range = undervalued.
  const undervalued = offer.buyPrice < offer.predictedLow;
  const disabled = owned || inventoryFull || !canAfford;

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: 12, borderRadius: 14,
        border: undervalued ? '1px solid var(--up)' : '1px solid var(--border)',
        background: owned ? 'var(--surface-3)' : 'var(--surface-2)', opacity: owned ? 0.6 : 1,
      }}
    >
      <span style={{ fontSize: 24, flexShrink: 0 }}>{offer.emoji}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 800, fontSize: 15, color: 'var(--text)' }}>
          {offer.name}
          {undervalued && !owned && <Chip tone="up">undervalued</Chip>}
        </span>
        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-2)' }}>
          Predicted resell {money(offer.predictedLow)}–{money(offer.predictedHigh)}
        </span>
      </span>
      <span style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Buy for</div>
        {owned ? (
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-3)' }}>✓ Bought</div>
        ) : (
          <Button tone="primary" size="sm" onClick={onBuy} disabled={disabled}>
            {!canAfford ? `${money(offer.buyPrice)} 🔒` : inventoryFull ? 'Full' : `Buy ${money(offer.buyPrice)}`}
          </Button>
        )}
      </span>
    </div>
  );
}
